import React, { useContext, useEffect, useState } from 'react';
import { RootState } from '../../Redux/Store';
import { useAppDispatch, useAppSelector } from '../../Hooks/Hooks';
import { AppContext } from '../../Context/AppContext';
import {
  getAllReports,
  getReportData,
  getReportId,
  getTaxForm,
  getTaxForms,
  updateFinancialInfoSectionSelectedTab,
} from '../../Redux/Reducers/FinancialInfoSlice';
import { getContentStackData } from '../../Redux/Reducers/ContentOrConfigSlice';
import { fbLogEvent } from '../../Helpers/FirebaseHelper';
import {
  arrayToObject,
  getBase64PDFfileURL,
  getContentFromUID,
  getContentStackAccordionContents,
} from '../../Utils/Basic';
import { CONTENTSTACK_PARAMS, FINANCIAL_INFO } from '../../Constants/Constants';
import { LEAD, SCREEN } from '../../Constants/AnalyticsConstants';
import { TFinancialInfoTabs } from './FinancialInfo.type';
import CustomTableFin from '../../Components/Shared/CustomTable/CustomTableFin';
import ToggleSearchTabs from '../../Components/ToggleSearchTabs/ToggleSearchTabs';
import './FinancialInfo.scss';
import { DeviceInfoDataRender } from '../../Utils/DeviceInfo';

const FinancialInfo = () => {
  const { isMobile } = useContext(AppContext);
  const [selectedTab, setSelectedTab] = useState<string>('');
  const [openedAccordion, setOpenedAccordion] = useState<any[]>([]);
  const [infoFormsAccordionContents, setInfoFormsAccordionContents] = useState<any[]>([]);
  const { financialInfoSectionSelectedTab, allReports, taxForms } = useAppSelector(
    (state: RootState) => state?.financialInfo,
  );
  const { showBanner, contentStackData } = useAppSelector(
    (state: RootState) => state.ContentOrConfig,
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(
      getContentStackData({
        queryPayload: {
          contentType: CONTENTSTACK_PARAMS.CONTENTTYPE_TABBED_CONTENT,
        },
      }),
    );
    dispatch(getAllReports({}));
  }, [dispatch]);

  useEffect(() => {
    const tabs = contentStackData?.tabbed_content?.find(
      (item: any) => item.uid === CONTENTSTACK_PARAMS.FINANCIAL_INFO_TABS_UID,
    ).tabs;
    const infoFormsAccordionContents = getContentStackAccordionContents(
      contentStackData,
      CONTENTSTACK_PARAMS.INFO_AND_FORMS_TABBED_ACCORDION_CONTENT_UID,
    );
    setInfoFormsAccordionContents(infoFormsAccordionContents);
    if (tabs) {
      dispatch(
        updateFinancialInfoSectionSelectedTab(
          tabs.map((item: any, index: number) => {
            const _content_uid = item.content[0].uid;
            const _content_type_uid = item.content[0]._content_type_uid;
            const _content =
              _content_type_uid === CONTENTSTACK_PARAMS.ACCORDION_CONTENT
                ? contentStackData.accordion_content
                : contentStackData.tabbed_accordion_content;
            const accordion_content = _content.find((item: any) => {
              return item.uid === _content_uid;
            });
            const financialInfoTab = {
              slug: item.content[0].uid,
              WebTitle: item.tab_title,
              MobTitle: item.tab_title,
              subTitle: accordion_content?.title,
              selected: !isMobile ? (index === 0 ? true : false) : false,
            };
            return financialInfoTab;
          }),
        ),
      );
      financialInfoSectionSelectedTab.map((item: TFinancialInfoTabs) => {
        item?.selected &&
          fbLogEvent('ue_' + item.slug, {
            screen: SCREEN.FINANCIAL_INFO,
            lead: isMobile ? LEAD.MORE : LEAD.LEFT_NAV,
            sectionName: 'ue_' + item.slug,
          });
      });
    }
  }, [contentStackData]);

  const handleTabChange = (slug: string) => {
    setSelectedTab(slug);

    if (slug === FINANCIAL_INFO.TAX_FORMS) {
      dispatch(getTaxForms({}));
    }
    
    dispatch(
      updateFinancialInfoSectionSelectedTab(
        financialInfoSectionSelectedTab?.map((item: TFinancialInfoTabs) => {
          let isSelected = item?.slug === slug;
          if (isMobile && item?.slug === slug) {
            isSelected = !item?.selected;
          }
          fbLogEvent('ue_' + slug, {
            screen: SCREEN.FINANCIAL_INFO,
            lead: isMobile ? LEAD.MORE : LEAD.LEFT_NAV,
            sectionName: 'ue_' + slug,
          });
          return { ...item, selected: isSelected };
        }),
      ),
    );
  };

  const getStatementData = (slug: string, param?: string) => {
    switch (slug) {
      case FINANCIAL_INFO.SLUG_SETTLEMENT: {
        const statementData = allReports.value?.find((item: any) => {
          return item?.hasOwnProperty(FINANCIAL_INFO.SETTLEMENT_DATE) ? item : null;
        });
        return param === 'id' ? statementData?.id : statementData?.settlementDate.details;
      }
      case FINANCIAL_INFO.SLUG_FUEL_TAX: {
        const statementData = allReports.value?.find((item: any) => {
          return item?.hasOwnProperty(FINANCIAL_INFO.FUEL_TAX_DATE) ? item : null;
        });
        return param === 'id' ? statementData?.id : statementData?.fuelTaxDate.details;
      }
      case FINANCIAL_INFO.SLUG_YTD: {
        const statementData = allReports.value?.find((item: any) => {
          return item?.hasOwnProperty(FINANCIAL_INFO.YTD_DATE) ? item : null;
        });
        return param === 'id' ? statementData?.id : statementData?.revenueDeductionDate.details;
      }
      case FINANCIAL_INFO.TAX_FORMS: {
        const taxformDetails = taxForms.taxformDetails;
        return taxformDetails;
      }
      default:
        return [];
    }
  };

  const handleAccordionToggle = (uid: string) => {
    if (openedAccordion.includes(uid)) {
      setOpenedAccordion(openedAccordion.filter((item) => item !== uid));
    } else {
      setOpenedAccordion([...openedAccordion, uid]);
    }
  };

  const handleClickEvent = (id: string, date: any, data?: any) => {
    const deviceInformation = arrayToObject(DeviceInfoDataRender());
    if (id === FINANCIAL_INFO.TAX_FORMS) {
      dispatch(
        getTaxForm({
          FChar: data.fchar,
          FormID: data.formId,
          RECID: data.tin,
          PayerID: data.payerId,
          year: date
        }),
      ).then((report) => {
        if (report?.payload) {
          const base64PDFfileURL = getBase64PDFfileURL(report.payload) || '';
          if (deviceInformation.browser_name === 'Safari') {
            const link = document.createElement('a');
            link.href = base64PDFfileURL;
            link.target = '_blank';
            link.download = 'report.pdf';
            link.click();
          } else {
            window?.open(base64PDFfileURL, '_blank');
          }
        } else {
          console.error('Error in fetching report data');
        }
      });

    } else {
      dispatch(
        getReportId({
          reportId: id,
          txnDate: date.date,
          format: 'pdf',
        }),
      ).then((response: any) => {
        if (response?.payload) {
          dispatch(
            getReportData({
              reportId: response?.payload.reportId,
              exportId: response?.payload.id,
            }),
          ).then((report) => {
            if (report?.payload) {
              const base64PDFfileURL = getBase64PDFfileURL(report.payload) || '';
              if (deviceInformation.browser_name === 'Safari') {
                const link = document.createElement('a');
                link.href = base64PDFfileURL;
                link.target = '_blank';
                link.download = 'report.pdf';
                link.click();
              } else {
                window?.open(base64PDFfileURL, '_blank');
              }
            } else {
              console.error('Error in fetching report data');
            }
          });
        }
      });
    }
  };

  const renderSupportContent = (isMobile: boolean = false) => {
    return (
      <div className="Tabbed_Accordion_Content">
        {infoFormsAccordionContents.map((item: any) => {
          return (
            <React.Fragment key={item?.uid}>
              <div className="Accordion_Content">
                <div className="Content_Title_Container">
                  <p className="Content_Title">{item?.heading}</p>
                  <div
                    className="Accordion_Arrow"
                    onClick={() => {
                      handleAccordionToggle(item?.uid);
                    }}
                  >
                    {!isMobile && (
                      <img
                        src={`/oo/assets/icon/grid-${
                          openedAccordion.includes(item?.uid) ? 'up' : 'down'
                        }-arrow-thin.svg`}
                        alt="Arrow icon"
                      />
                    )}
                  </div>
                </div>

                {(openedAccordion.includes(item?.uid) || isMobile) && (
                  <>
                    {!!item.content ? (
                      <div
                        className="content_data content"
                        dangerouslySetInnerHTML={{
                          __html: getContentFromUID(item?.uid, contentStackData)?.content,
                        }}
                      />
                    ) : (
                      <div>No data available.</div>
                    )}
                  </>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className={`FinancialInfo${showBanner ? ' AlertBannerVisible' : ''}`}
      data-testid="FinancialInfoContainer"
    >
      {!isMobile && (
        <p
          className="FinancialInfo_Header_Web"
          id="FinancialInfo_Header_Web"
          data-testid="FinancialInfo_Header_Web"
        >
          Financial Info
        </p>
      )}
      {!isMobile && (
        <div>
          <ToggleSearchTabs
            data={financialInfoSectionSelectedTab}
            handleTabChange={handleTabChange}
          />
          <div className={`Report ${isMobile ? 'small' : 'large'} ${selectedTab === FINANCIAL_INFO.SLUG_INFO_AND_FORMS ? 'Content_Data' : ''}`}>
            <div className="header">
              {financialInfoSectionSelectedTab?.map((item: any) => {
                if (isMobile && !item?.MobTitle) return null;
                return (
                  item.selected && (
                    <div
                      className="Left_Col"
                      id="Financial_Report_Header_Web"
                      data-testid="Financial_Report_Header_Web"
                    >
                      {item?.subTitle}
                    </div>
                  )
                );
              })}
              {/* <div className='Right_Col'>
              <div className='Refresh_Logo'>
                <img src='/oo/assets/icon/refresh.svg' alt='refreshIcon' height={'17px'} width={'17px'} />
              </div>
              <div className='Refresh_Title'>
                Refresh</div>
            </div> */}
            </div>

            {financialInfoSectionSelectedTab?.map((item: any) => {
              if (item.selected && item.slug === FINANCIAL_INFO.SLUG_INFO_AND_FORMS) {
              }

              return (
                item.selected &&
                (item.slug === FINANCIAL_INFO.SLUG_INFO_AND_FORMS ? (
                  <div>
                    <div className="Support_Content_Container">{renderSupportContent()}</div>
                  </div>
                ) : (
                  <CustomTableFin
                    id={item.slug === FINANCIAL_INFO.TAX_FORMS ? FINANCIAL_INFO.TAX_FORMS : getStatementData(item.selected && item.slug, 'id')}
                    data={getStatementData(item.selected && item.slug, 'dates')}
                    tableLabel={''}
                    additionalClass="Report_Details"
                    callback={handleClickEvent}
                    rowdata={item.subTitle}
                  />
                ))
              );
            })}
          </div>
        </div>
      )}

      {isMobile &&
        financialInfoSectionSelectedTab?.map((item: any) => {
          return (
            <div
              className="FinancialInfo_Container_Mobile"
            >
              <div className="Cards_Container" onClick={() => handleTabChange(item?.slug)}>
                <div className="Card" onClick={() => {}}>
                  <p>{item.MobTitle}</p>
                  <img
                    src={`/oo/assets/icon/${item.selected ? 'minus-icon' : 'plus'}.svg`}
                    alt="Financial Info"
                  />
                </div>
              </div>

              {item.selected &&
                (item.slug === FINANCIAL_INFO.SLUG_INFO_AND_FORMS ? (
                  <div>
                    <div className="Support_Content_Container">
                      {renderSupportContent(isMobile)}
                    </div>
                  </div>
                ) : (
                  <div className={`Report ${isMobile ? 'small' : 'large'}`}>
                    <CustomTableFin
                      id={item.slug === FINANCIAL_INFO.TAX_FORMS ? FINANCIAL_INFO.TAX_FORMS : getStatementData(item.selected && item.slug, 'id')}
                      data={getStatementData(item.selected && item.slug, 'dates')}
                      tableLabel={''}
                      additionalClass="Report_Details"
                      callback={handleClickEvent}
                      rowdata={item.subTitle}
                    />
                  </div>
                ))}
            </div>
          );
        })}
    </div>
  );
};

export default FinancialInfo;
